import { toSupportedLanguage } from '@wundo/shared/language.ts';
import { getLocale, isLocale, setLocale } from '@/paraglide/runtime.js';

/**
 * Tell the document which language it is actually in.
 *
 * `index.html` can only carry one `lang`, and it said `nl` for everyone —
 * including an English or French reader. That attribute is what a screen reader
 * picks its pronunciation rules from, and what a browser reads before offering
 * to translate the page, so a wrong one is worse than a missing one. Set it from
 * the locale paraglide actually resolved, on every load.
 */
export function applyDocumentLanguage(): void {
  document.documentElement.lang = getLocale();
}

/**
 * Once someone is signed in, their stored language wins over the browser's.
 *
 * The configured strategy is `cookie → preferredLanguage → baseLocale`, so an
 * anonymous visitor still gets the language their browser asks for — that is
 * the right guess for a sign-in page. It stops being a guess the moment we know
 * who they are: a Dutch-speaking user on a borrowed English laptop set their
 * language for a reason, and the browser cannot know it.
 *
 * `setLocale` writes the cookie the first strategy reads and reloads, so the
 * next load resolves to the same locale and this becomes a no-op. That is what
 * stops it looping.
 */
export function applyUserLocale(language: string | undefined): void {
  if (!language) return;

  /* A stored language we no longer ship (German) reads English, as everywhere else. */
  const locale = toSupportedLanguage(language);
  if (locale === getLocale() || !isLocale(locale)) return;

  setLocale(locale);
}
